import { Suspense } from "react"
import { Pledge, GivingPerson } from "../types"
import { Loading } from "source/shared/components"
import PledgeDetails from "./PledgeDetails"
import InfiniteScroll from "react-infinite-scroller"
import { usePledges } from "../hooks/usePledges"
import ConcludedPledgesList from "./ConcludedPledgesList"
import { css } from "@emotion/react"
import { Heading } from "@planningcenter/doxy-web"
import { useGivingPersonContext } from "../hooks/useGivingPersonContext"

interface PledgesListProps {
  showFullDetails: boolean
}

export default function PledgesList({ showFullDetails }: PledgesListProps) {
  const { givingPerson } = useGivingPersonContext()

  const { data, fetchNextPage, hasNextPage, isFetching } = usePledges({
    current: true,
  })

  const currentPledges: Pledge[] =
    data.pages.flatMap((page) => page.pledges) || []
  const jointGiver: GivingPerson | null = data.pages[0]?.jointGiver || null
  const loadMore = () => !isFetching && fetchNextPage()

  const pledgesHeaderText = jointGiver
    ? `Pledges for ${givingPerson.first_name} and ${jointGiver.first_name}`
    : "Pledges"

  const hasConcludedPledges = data.pages[0]?.meta
    .has_concluded_pledges as boolean

  const currentPledgesContent = (
    <div className="d-f fd-c g-2">
      {currentPledges.length > 0 && (
        <div className="d-f fd-c g-1">
          <div className="fw-600 fs-13 tt-u c-tint2">Current pledges</div>
          <InfiniteScroll
            hasMore={hasNextPage}
            loadMore={loadMore}
            loader={<Loading key="loading" />}
            className="d-f fd-c g-2"
          >
            {currentPledges.map((pledge) => (
              <PledgeDetails
                key={pledge.id}
                jointGiver={jointGiver}
                pledge={pledge}
                pledgeCampaign={pledge.pledge_campaign}
                fund={pledge.fund}
                showFullDetails={showFullDetails}
              />
            ))}
          </InfiniteScroll>
        </div>
      )}
    </div>
  )

  if (currentPledges.length === 0 && !hasConcludedPledges) return

  return showFullDetails ? (
    <div
      className="d-f fd-c p-2 g-2"
      css={css`
        border: 1px solid var(--color-tint6);
        border-radius: 8px;
      `}
    >
      <Heading level={2} size={3} text={pledgesHeaderText} />
      {currentPledgesContent}
      {hasConcludedPledges && (
        <Suspense fallback={<Loading />}>
          <ConcludedPledgesList />
        </Suspense>
      )}
    </div>
  ) : (
    currentPledgesContent
  )
}
